<template>
  <div class="stock-bulk-import-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="5000">
        <CToast
          v-for="info in infoList"
          :key="info.id"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Main Content -->
    <CRow>
      <CCol>
        <CCard class="main-card">
          <CCardHeader class="professional-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="header-title">
                <h5 class="mb-0">
                  <i class="fas fa-file-upload me-2"></i>
                  <strong>Bulk Stock Import</strong>
                </h5>
                <small class="text-muted">Import multiple stock records via CSV file</small>
              </div>
              <div class="header-actions">
                <CButton
                  color="info"
                  variant="outline"
                  size="sm"
                  @click="downloadTemplate"
                >
                  <i class="fas fa-download me-2"></i>
                  Download Template
                </CButton>
              </div>
            </div>
          </CCardHeader>

          <CCardBody>
            <!-- Step 1: Upload -->
            <div v-if="currentStep === 1">
              <div class="upload-section">
                <h6 class="mb-3">
                  <i class="fas fa-cloud-upload-alt me-2"></i>
                  Step 1: Upload CSV File
                </h6>
                <div class="upload-area" @drop.prevent="handleDrop" @dragover.prevent>
                  <input
                    ref="fileInput"
                    type="file"
                    accept=".csv"
                    @change="handleFileSelect"
                    style="display: none"
                  />
                  <div v-if="!selectedFile" class="upload-placeholder" @click="$refs.fileInput.click()">
                    <i class="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
                    <p class="mb-2"><strong>Click to upload</strong> or drag and drop</p>
                    <p class="text-muted small">CSV files only</p>
                  </div>
                  <div v-else class="file-selected">
                    <i class="fas fa-file-csv fa-2x text-success mb-2"></i>
                    <p class="mb-1"><strong>{{ selectedFile.name }}</strong></p>
                    <p class="text-muted small">{{ formatFileSize(selectedFile.size) }}</p>
                    <CButton
                      color="danger"
                      variant="outline"
                      size="sm"
                      @click="clearFile"
                      class="mt-2"
                    >
                      <i class="fas fa-times me-1"></i>
                      Remove
                    </CButton>
                  </div>
                </div>

                <div class="d-flex justify-content-end mt-4">
                  <CButton
                    color="primary"
                    @click="validateFile"
                    :disabled="!selectedFile || validating"
                  >
                    <CSpinner v-if="validating" size="sm" class="me-2" />
                    <i v-else class="fas fa-check-circle me-2"></i>
                    {{ validating ? 'Validating...' : 'Validate & Continue' }}
                  </CButton>
                </div>
              </div>
            </div>

            <!-- Step 2: Validation Results -->
            <div v-if="currentStep === 2">
              <div class="validation-section">
                <h6 class="mb-3">
                  <i class="fas fa-clipboard-check me-2"></i>
                  Step 2: Validation Results
                </h6>

                <!-- Summary Cards -->
                <CRow class="mb-4">
                  <CCol md="3">
                    <CCard class="summary-card border-primary">
                      <CCardBody class="text-center">
                        <h3 class="mb-1 text-primary">{{ validationResult.totalRows || 0 }}</h3>
                        <small class="text-muted">Total Rows</small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol md="3">
                    <CCard class="summary-card border-success">
                      <CCardBody class="text-center">
                        <h3 class="mb-1 text-success">{{ validationResult.validRows || 0 }}</h3>
                        <small class="text-muted">Valid Rows</small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol md="3">
                    <CCard class="summary-card border-danger">
                      <CCardBody class="text-center">
                        <h3 class="mb-1 text-danger">{{ validationResult.invalidRows || 0 }}</h3>
                        <small class="text-muted">Invalid Rows</small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                  <CCol md="3">
                    <CCard class="summary-card border-info">
                      <CCardBody class="text-center">
                        <h3 class="mb-1 text-info">{{ validationResult.summary?.newStocks || 0 }}</h3>
                        <small class="text-muted">New Stocks</small>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>

                <!-- Error Details -->
                <div v-if="validationResult.errors && validationResult.errors.length > 0" class="mb-4">
                  <h6 class="text-danger mb-3">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    Validation Errors ({{ validationResult.errors.length }})
                  </h6>
                  <div class="error-list">
                    <CAlert
                      v-for="(error, index) in validationResult.errors.slice(0, 20)"
                      :key="index"
                      color="danger"
                      class="error-item"
                    >
                      <strong>Row {{ error.row }}</strong> - {{ error.field }}: {{ error.error }}
                      <span v-if="error.value" class="text-muted"> (Value: "{{ error.value }}")</span>
                    </CAlert>
                    <CAlert v-if="validationResult.errors.length > 20" color="warning">
                      Showing first 20 errors. Total: {{ validationResult.errors.length }} errors.
                    </CAlert>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="d-flex justify-content-between mt-4">
                  <CButton
                    color="secondary"
                    variant="outline"
                    @click="goBackToUpload"
                  >
                    <i class="fas fa-arrow-left me-2"></i>
                    Back
                  </CButton>
                  <div>
                    <CButton
                      v-if="validationResult.validRows > 0"
                      color="success"
                      @click="confirmImport"
                      :disabled="importing"
                    >
                      <CSpinner v-if="importing" size="sm" class="me-2" />
                      <i v-else class="fas fa-upload me-2"></i>
                      {{ importing ? 'Importing...' : `Import ${validationResult.validRows} Valid Stocks` }}
                    </CButton>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Import Complete -->
            <div v-if="currentStep === 3">
              <div class="complete-section text-center py-5">
                <i
                  :class="importResult.success ? 'fas fa-check-circle fa-4x text-success mb-4' : 'fas fa-times-circle fa-4x text-danger mb-4'"
                ></i>
                <h4 class="mb-3">{{ importResult.success ? 'Import Successful!' : 'Import Failed' }}</h4>
                <p class="text-muted mb-4">{{ importResult.message }}</p>

                <CRow class="mb-4" v-if="importResult.success">
                  <CCol md="6" class="offset-md-3">
                    <CCard class="border-success">
                      <CCardBody>
                        <div class="d-flex justify-content-between mb-2">
                          <span>Imported:</span>
                          <strong class="text-success">{{ importResult.importedCount }}</strong>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>Failed:</span>
                          <strong class="text-danger">{{ importResult.failedCount }}</strong>
                        </div>
                      </CCardBody>
                    </CCard>
                  </CCol>
                </CRow>

                <div class="mt-4">
                  <CButton
                    color="primary"
                    @click="resetImport"
                    class="me-2"
                  >
                    <i class="fas fa-redo me-2"></i>
                    Import Another File
                  </CButton>
                  <CButton
                    color="secondary"
                    variant="outline"
                    @click="goToStockList"
                  >
                    <i class="fas fa-list me-2"></i>
                    View Stock List
                  </CButton>
                </div>
              </div>
            </div>
          </CCardBody>

          <CCardFooter class="card-footer">
            <div class="d-flex justify-content-between align-items-center">
              <small class="text-muted">
                <i class="fas fa-info-circle me-1"></i>
                Download template to see required CSV format
              </small>
              <div class="step-indicator">
                <CBadge :color="currentStep >= 1 ? 'primary' : 'secondary'" class="me-1">1. Upload</CBadge>
                <CBadge :color="currentStep >= 2 ? 'primary' : 'secondary'" class="me-1">2. Validate</CBadge>
                <CBadge :color="currentStep >= 3 ? 'success' : 'secondary'">3. Import</CBadge>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import StockApi from "../../lib/stockApi";

export default {
  name: "StockBulkImport",
  data() {
    return {
      currentStep: 1,
      selectedFile: null,
      validating: false,
      importing: false,
      validationResult: {},
      importResult: {},
      infoList: [],
      api: new StockApi(),
    };
  },
  methods: {
    toast(header, message, color) {
      const toastId = `toast-${Date.now()}-${Math.random()}`;
      this.infoList.push({
        id: toastId,
        header: header,
        message: message,
        color: color,
      });

      // Auto-remove toast after autohide duration (5000ms + small buffer)
      setTimeout(() => {
        const index = this.infoList.findIndex(info => info.id === toastId);
        if (index !== -1) {
          this.infoList.splice(index, 1);
        }
      }, 5500);
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.name.endsWith('.csv')) {
          this.selectedFile = file;
        } else {
          this.toast("Error", "Please select a CSV file", "danger");
        }
      }
    },

    handleDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file) {
        if (file.name.endsWith('.csv')) {
          this.selectedFile = file;
        } else {
          this.toast("Error", "Please select a CSV file", "danger");
        }
      }
    },

    clearFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = '';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    async validateFile() {
      if (!this.selectedFile) return;

      this.validating = true;
      try {
        const result = await this.api.bulkUpload(this.selectedFile);
        console.log("[FRONTEND] Validation result:", result);
        this.validationResult = result;
        this.currentStep = 2;

        if (result.validRows === 0) {
          this.toast("Warning", "No valid rows found in CSV", "warning");
        } else {
          this.toast("Success", `Validation complete: ${result.validRows} valid rows`, "success");
        }
      } catch (error) {
        this.toast("Error", error.response?.data?.message || "Failed to validate file", "danger");
      } finally {
        this.validating = false;
      }
    },

    async confirmImport() {
      console.log("[FRONTEND] ========== CONFIRM IMPORT BUTTON CLICKED ==========");
      console.log("[FRONTEND] validationResult:", this.validationResult);

      if (!this.validationResult.validationToken) {
        console.error("[FRONTEND] No validation token found!");
        return;
      }

      console.log("[FRONTEND] confirmImport called");
      console.log("[FRONTEND] validationToken:", this.validationResult.validationToken);

      this.importing = true;
      try {
        console.log("[FRONTEND] Calling api.confirmImport...");
        const result = await this.api.confirmImport({
          validationToken: this.validationResult.validationToken,
          importOnlyValid: true,
        });
        console.log("[FRONTEND] Import result received:", result);

        this.importResult = result;
        this.currentStep = 3;

        if (result.success) {
          this.toast("Success", result.message, "success");
        } else {
          this.toast("Error", result.message, "danger");
        }
      } catch (error) {
        console.error("[FRONTEND] Import error:", error);
        console.error("[FRONTEND] Error response:", error.response);
        console.error("[FRONTEND] Error response data:", error.response?.data);
        console.error("[FRONTEND] Error response status:", error.response?.status);
        console.error("[FRONTEND] Error response headers:", error.response?.headers);
        console.error("[FRONTEND] Full error object:", JSON.stringify(error, null, 2));
        this.importResult = {
          success: false,
          message: error.response?.data?.message || "Failed to import stocks",
          importedCount: 0,
          failedCount: 0,
        };
        this.currentStep = 3;
        this.toast("Error", this.importResult.message, "danger");
      } finally {
        this.importing = false;
        console.log("[FRONTEND] confirmImport finished");
      }
    },

    async downloadTemplate() {
      try {
        await this.api.downloadTemplate();
        this.toast("Success", "Template downloaded successfully", "success");
      } catch (error) {
        this.toast("Error", "Failed to download template", "danger");
      }
    },

    goBackToUpload() {
      this.currentStep = 1;
      this.validationResult = {};
    },

    resetImport() {
      this.currentStep = 1;
      this.selectedFile = null;
      this.validationResult = {};
      this.importResult = {};
      this.$refs.fileInput.value = '';
    },

    goToStockList() {
      this.$router.push({ path: "/admins/StockList" });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.stock-bulk-import-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Main Card */
.main-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Professional Header */
.professional-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1.25rem 1.5rem;
}

.header-title h5 {
  font-size: 1.25rem;
  color: var(--cui-body-color, #212529);
  margin-bottom: 0.25rem;
}

.header-title small {
  font-size: 0.875rem;
}

/* Upload Area */
.upload-area {
  border: 2px dashed var(--cui-border-color, #d8dbe0);
  border-radius: 0.375rem;
  padding: 3rem 2rem;
  text-align: center;
  background: var(--cui-body-bg, #f8f9fa);
  transition: all 0.3s ease;
}

.upload-area:hover {
  border-color: var(--cui-primary, #321fdb);
  background: white;
}

.upload-placeholder {
  cursor: pointer;
}

.file-selected {
  padding: 1rem;
}

/* Summary Cards */
.summary-card {
  transition: transform 0.2s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
}

/* Error List */
.error-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 0.5rem;
  background: var(--cui-body-bg, #f8f9fa);
  border-radius: 0.375rem;
}

.error-item {
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  padding: 0.75rem;
}

.error-item:last-child {
  margin-bottom: 0;
}

/* Step Indicator */
.step-indicator {
  display: flex;
  gap: 0.5rem;
}

/* Footer */
.card-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.5rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .stock-bulk-import-wrapper {
    padding: 1rem;
  }

  .professional-header {
    padding: 1rem;
  }

  .header-title h5 {
    font-size: 1.125rem;
  }

  .upload-area {
    padding: 2rem 1rem;
  }

  .step-indicator {
    flex-direction: column;
    gap: 0.25rem;
  }
}
</style>
